import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2, LockKeyhole, Mail, MapPin, Pencil, QrCode, Truck } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { formatMoney } from "@melhot/shared/money";
import { checkoutSchema } from "@melhot/validation";
import type { Checkout, ShippingRate } from "@melhot/shared";
import type { z } from "zod";
import { api } from "../api";
import { CheckoutLayout } from "../components/CheckoutLayout";
import { OrderSummary } from "../components/OrderSummary";
import { useCheckoutStore } from "../store";
import { googleAdsTracker } from "../services/googleAds";

type FormValues = z.infer<typeof checkoutSchema>;
type CheckoutStep = "contact" | "shipping" | "payment";

const stepOrder: CheckoutStep[] = ["contact", "shipping", "payment"];

const fallbackShippingRates: ShippingRate[] = [
  { id: "economico", carrier: "MelHot", serviceCode: "PAC", label: "PAC", amount: 499, currency: "BRL", estimatedDays: 7 },
  { id: "sedex", carrier: "MelHot", serviceCode: "SEDEX", label: "SEDEX", amount: 899, currency: "BRL", estimatedDays: 3 },
  { id: "motoboy", carrier: "MelHot", serviceCode: "MOTOBOY", label: "Motoboy", amount: 1299, currency: "BRL", estimatedDays: 1 }
];

function readShopifyCartPayload() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get("cart");
  if (!encoded) return null;
  try {
    const normalized = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const json = decodeURIComponent(
      Array.from(atob(padded))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getInitialCep(): string {
  return "";
}

function cartPayloadToInitialSession(cartPayload: any): Checkout | null {
  if (!cartPayload || !cartPayload.items?.length) return null;
  const items = cartPayload.items.map((item: any, idx: number) => {
    const variantId = String(item.variantId || "").replace(/\D/g, "");
    const productId = String(item.productId || "0").replace(/\D/g, "");
    const unitPrice = Number(item.unitPrice) || 0;
    const quantity = Number(item.quantity) || 1;
    return {
      id: `item_preview_${idx}_${variantId}`,
      shopifyProductGid: `gid://shopify/Product/${productId || "0"}`,
      shopifyVariantGid: `gid://shopify/ProductVariant/${variantId}`,
      sku: item.sku,
      title: item.title,
      variantTitle: item.variantTitle,
      imageUrl: item.imageUrl,
      quantity,
      unitPrice,
      discountAmount: 0,
      finalUnitPrice: unitPrice
    };
  });
  const subtotal = items.reduce((sum: number, it: any) => sum + it.unitPrice * it.quantity, 0);
  const discountAmount = Math.floor(subtotal * 0.05);
  return {
    id: "preview",
    publicId: "preview",
    status: "OPEN",
    subtotal,
    discountAmount,
    shippingAmount: 0,
    total: subtotal - discountAmount,
    currency: (cartPayload.currency as any) || "BRL",
    selectedShippingMethod: "economico",
    items,
    lastActivityAt: new Date().toISOString()
  };
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { checkoutId, setCheckoutId, setPaymentId } = useCheckoutStore();
  const [error, setError] = useState<string | null>(null);
  const [addressLookupStatus, setAddressLookupStatus] = useState<"idle" | "loading" | "found" | "manual">("idle");
  const [lastCepLookup, setLastCepLookup] = useState("");
  const [activeStep, setActiveStep] = useState<CheckoutStep>("contact");
  const [completedSteps, setCompletedSteps] = useState<CheckoutStep[]>([]);
  const [showAddressEdit, setShowAddressEdit] = useState(false);
  const [editingReview, setEditingReview] = useState<"contact" | "address" | "shipping" | null>(null);
  const [noNumber, setNoNumber] = useState(false);
  const numberInputRef = useRef<HTMLInputElement | null>(null);

  const initialPayload = useMemo(() => readShopifyCartPayload(), []);
  const initialSession = useMemo(() => cartPayloadToInitialSession(initialPayload), [initialPayload]);

  const sessionQuery = useQuery({
    queryKey: ["checkout", checkoutId, window.location.search],
    initialData: initialSession ? (initialSession as any) : undefined,
    queryFn: async () => {
      const cartPayload = readShopifyCartPayload();
      if (cartPayload) {
        const created = await api.createSessionFromShopifyCart(cartPayload);
        setCheckoutId(created.publicId);
        window.history.replaceState({}, "", window.location.pathname);
        return created;
      }
      const recoveryId = new URLSearchParams(window.location.search).get("recover");
      if (recoveryId) {
        setCheckoutId(recoveryId);
        return api.getSession(recoveryId);
      }
      if (checkoutId) return api.getSession(checkoutId);
      const created = await api.createSession();
      setCheckoutId(created.publicId);
      return created;
    }
  });

  const configQuery = useQuery({ queryKey: ["checkout-config"], queryFn: api.getConfig, staleTime: 300000 });

  useEffect(() => {
    if (configQuery.data?.googleAds) {
      googleAdsTracker.init(configQuery.data.googleAds);
    }
  }, [configQuery.data]);

  useEffect(() => {
    if (sessionQuery.data?.items?.length) {
      const items = sessionQuery.data.items.map((item: any) => ({
        id: item.shopifyVariantGid || item.id,
        name: item.title,
        price: item.unitPrice,
        quantity: item.quantity
      }));
      googleAdsTracker.trackBeginCheckout(items, sessionQuery.data.total, sessionQuery.data.currency || "BRL");
    }
  }, [sessionQuery.data]);

  const form = useForm<FormValues>({
    resolver: zodResolver(checkoutSchema),
    mode: "onBlur",
    defaultValues: { address: { country: "BR", postalCode: "" } as FormValues["address"] }
  });

  const postalCode = form.watch("address.postalCode");
  const normalizedPostalCode = (postalCode ?? "").replace(/\D/g, "");
  const phone = form.watch("phone");
  const cpf = form.watch("cpf");
  const selectedShippingMethod = form.watch("selectedShippingMethod");
  const postalCodeField = form.register("address.postalCode");
  const numberField = form.register("address.number");

  useEffect(() => {
    const session = sessionQuery.data;
    const storedProfile = getStoredProfile();
    const storedEmail = getStoredEmail();
    if (storedEmail && !form.getValues("email")) form.setValue("email", storedEmail);
    if (storedProfile) {
      if (storedProfile.email && !form.getValues("email")) form.setValue("email", storedProfile.email);
      if (storedProfile.phone && !form.getValues("phone")) form.setValue("phone", formatPhone(storedProfile.phone));
      if (storedProfile.firstName && !form.getValues("firstName")) form.setValue("firstName", storedProfile.firstName);
      if (storedProfile.lastName && !form.getValues("lastName")) form.setValue("lastName", storedProfile.lastName);
      if (storedProfile.selectedShippingMethod && !form.getValues("selectedShippingMethod")) form.setValue("selectedShippingMethod", storedProfile.selectedShippingMethod);
      if (storedProfile.address) {
        const currentAddress = form.getValues("address");
        const mergedPostalCode = storedProfile.address.postalCode || currentAddress.postalCode || "";
        form.setValue("address", { ...currentAddress, ...storedProfile.address, postalCode: mergedPostalCode, country: "BR" });
      }
    }
    if (!session) return;
    if (session.email && !form.getValues("email")) form.setValue("email", session.email);
    form.setValue("items", session.items as any);
    form.setValue("address.country", "BR");
  }, [sessionQuery.data, form]);

  useEffect(() => {
    const digits = (postalCode ?? "").replace(/\D/g, "");
    if (digits.length !== 8 || digits === lastCepLookup) return;
    setLastCepLookup(digits);
    void lookupCep(digits);
  }, [postalCode, lastCepLookup]);

  const ratesQuery = useQuery({
    queryKey: ["shipping", normalizedPostalCode, sessionQuery.data?.publicId],
    enabled: activeStep !== "contact" && normalizedPostalCode.length === 8,
    queryFn: () =>
      api.shippingRates({
        postalCode: normalizedPostalCode,
        address: form.getValues("address"),
        items: sessionQuery.data?.items?.length ? sessionQuery.data.items : fallbackCheckoutItems()
      }),
    retry: 1
  });

  const pixMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const session = sessionQuery.data!;
      await api.updateSession(session.publicId, values);
      return api.createPix(session.publicId);
    },
    onSuccess: (payment) => {
      setPaymentId(payment.id);
      navigate("/checkout/pix");
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Erro ao finalizar compra")
  });

  async function lookupCep(value = form.getValues("address.postalCode")) {
    const digits = (value ?? "").replace(/\D/g, "");
    if (digits.length !== 8) return;
    setAddressLookupStatus("loading");
    const address = (await api.lookupAddress(digits).catch(() => null)) ?? (await lookupAddressFallback(digits));
    if (address) {
      form.setValue("address.street", address.street);
      form.setValue("address.neighborhood", address.neighborhood);
      form.setValue("address.city", address.city);
      form.setValue("address.state", address.state);
      form.setValue("address.country", "BR");
      setAddressLookupStatus("found");
    } else {
      setAddressLookupStatus("manual");
      setShowAddressEdit(true);
    }
  }

  function completeStep(step: CheckoutStep) {
    setCompletedSteps((steps) => (steps.includes(step) ? steps : [...steps, step]));
    const next = stepOrder[stepOrder.indexOf(step) + 1];
    if (next) setActiveStep(next);
  }

  async function savePartial(values: unknown) {
    if (!sessionQuery.data) return;
    await api.updateSession(sessionQuery.data.publicId, values);
    void sessionQuery.refetch();
  }

  async function continueContact() {
    const emailValid = await form.trigger("email");
    if (!emailValid) return;
    if (normalizedPostalCode.length !== 8) {
      form.setError("address.postalCode", { message: "Informe um CEP válido." });
      return;
    }
    form.clearErrors("address.postalCode");
    const email = form.getValues("email");
    void lookupCep(normalizedPostalCode);
    persistEmail(email);
    persistProfile({ email, address: { postalCode: normalizedPostalCode, country: "BR" } });
    void savePartial({ email }).catch(() => undefined);
    completeStep("contact");
    void ratesQuery.refetch().catch(() => undefined);
  }

  async function continueShipping() {
    if (normalizedPostalCode.length === 8 && addressLookupStatus !== "found") {
      await lookupCep(normalizedPostalCode);
    }
    const valid = await form.trigger(["selectedShippingMethod", "firstName", "lastName", "phone", "address"]);
    if (!valid) return;
    await savePartial({
      phone: form.getValues("phone"),
      firstName: form.getValues("firstName"),
      lastName: form.getValues("lastName"),
      address: form.getValues("address"),
      selectedShippingMethod: form.getValues("selectedShippingMethod"),
      shippingAmount: selectedRate?.amount ?? 0
    });
    persistProfile({
      email: form.getValues("email"),
      phone: form.getValues("phone"),
      firstName: form.getValues("firstName"),
      lastName: form.getValues("lastName"),
      selectedShippingMethod: form.getValues("selectedShippingMethod"),
      address: form.getValues("address")
    });
    completeStep("shipping");
  }

  function submit(values: FormValues) {
    setError(null);
    if (!sessionQuery.data) {
      setError("Estamos carregando seu carrinho. Tente novamente em instantes.");
      return;
    }
    persistProfile({
      email: values.email,
      phone: values.phone,
      firstName: values.firstName,
      lastName: values.lastName,
      selectedShippingMethod: values.selectedShippingMethod,
      address: values.address
    });
    pixMutation.mutate(values);
  }

  function handleSubmitError(errors: typeof form.formState.errors) {
    const firstMissing =
      errors.email ? "email" :
      errors.address?.postalCode ? "address.postalCode" :
      errors.selectedShippingMethod ? "selectedShippingMethod" :
      errors.firstName ? "firstName" :
      errors.lastName ? "lastName" :
      errors.phone ? "phone" :
      errors.address?.street ? "address.street" :
      errors.address?.neighborhood ? "address.neighborhood" :
      errors.address?.city ? "address.city" :
      errors.address?.state ? "address.state" :
      errors.address?.number ? "address.number" :
      errors.cpf ? "cpf" :
      undefined;

    if (errors.email || errors.address?.postalCode) setActiveStep("contact");
    else if (errors.selectedShippingMethod || errors.firstName || errors.lastName || errors.phone || errors.address) {
      setActiveStep("shipping");
      if (errors.address) setShowAddressEdit(true);
    }
    else if (errors.cpf) setActiveStep("payment");

    setError("Preencha os dados obrigatórios para finalizar sua compra.");

    window.setTimeout(() => {
      if (firstMissing) form.setFocus(firstMissing as any);
      if (firstMissing) document.querySelector<HTMLInputElement>(`[name="${firstMissing}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  }

  function handlePostalCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
    const formatted = formatPostalCode(event.target.value);
    form.setValue("address.postalCode", formatted, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: formatted.replace(/\D/g, "").length === 8
    });
  }

  function handlePhoneChange(event: React.ChangeEvent<HTMLInputElement>) {
    const formatted = formatPhone(event.target.value);
    form.setValue("phone", formatted, { shouldDirty: true, shouldTouch: true, shouldValidate: formatted.replace(/\D/g, "").length >= 10 });
  }

  function handleCpfChange(event: React.ChangeEvent<HTMLInputElement>) {
    const formatted = formatCpf(event.target.value);
    form.setValue("cpf", formatted, { shouldDirty: true, shouldTouch: true, shouldValidate: formatted.replace(/\D/g, "").length === 11 });
  }

  const checkout = sessionQuery.data;
  const availableRates = ratesQuery.data?.length ? ratesQuery.data : activeStep !== "contact" && normalizedPostalCode.length === 8 ? fallbackShippingRates : undefined;
  const selectedRate = availableRates?.find((rate) => rate.id === selectedShippingMethod) || availableRates?.[0];

  useEffect(() => {
    if (availableRates?.length && !selectedShippingMethod) {
      form.setValue("selectedShippingMethod", availableRates[0].id);
    }
  }, [availableRates, selectedShippingMethod]);
  const pixDiscountAmount = checkout ? Math.floor(checkout.subtotal * 0.05) : 0;
  const preview = checkout
    ? {
        ...checkout,
        discountAmount: pixDiscountAmount,
        selectedShippingMethod,
        shippingAmount: selectedRate?.amount ?? checkout.shippingAmount,
        total: checkout.subtotal - pixDiscountAmount + (selectedRate?.amount ?? checkout.shippingAmount)
      }
    : undefined;
  const addressSummary = [form.watch("address.street"), form.watch("address.neighborhood"), form.watch("address.city"), form.watch("address.state")]
    .filter(Boolean)
    .join(", ");
  const contactSummary = form.watch("email");
  const shippingSummary = selectedRate ? `${selectedRate.label} - ${formatDeliveryTime(selectedRate.id, selectedRate.estimatedDays)}` : "";

  if (sessionQuery.isPending && !preview) {
    return (
      <main className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent mb-4" />
        <p className="text-base font-semibold text-slate-800">Carregando seu checkout seguro...</p>
        <p className="text-xs text-slate-500 mt-1">Por favor, aguarde alguns instantes.</p>
      </main>
    );
  }

  if (sessionQuery.isError && !preview) {
    return (
      <main className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <div className="rounded-full bg-red-100 p-3 text-red-600 mb-4">
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-1">Ops! Ocorreu um problema ao carregar seu carrinho.</h2>
        <p className="text-sm text-slate-600 mb-6 max-w-sm">Tente recarregar a página ou volte para a loja para selecionar seus produtos.</p>
        <div className="flex gap-3">
          <button onClick={() => window.location.reload()} className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
            Tentar novamente
          </button>
          <a href="https://melhot.com.br" className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Voltar para a loja
          </a>
        </div>
      </main>
    );
  }

  return (
    <CheckoutLayout
      main={
        <form className="px-4 pb-6 pt-3 sm:px-0 lg:pt-0" onSubmit={form.handleSubmit(submit, handleSubmitError)}>
          {activeStep !== "contact" && <PromoTimer />}
          <CheckoutProgress activeStep={activeStep} completedSteps={completedSteps} />

          <div className="space-y-7">
          <StepSection active={activeStep === "contact"} completed={completedSteps.includes("contact")} title="Dados de contato" summary={contactSummary} onEdit={() => setActiveStep("contact")}>
            <div className="grid gap-3">
              <Field label="E-mail" error={form.formState.errors.email?.message}>
                <input className="field" type="email" autoComplete="email" placeholder="E-mail" {...form.register("email")} />
              </Field>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="h-4 w-4 border-slate-300 accent-trust" />
                Receber ofertas e novidades por e-mail
              </label>
              <div className="pt-5">
                <h2 className="mb-3 text-sm font-medium uppercase text-slate-900">Entrega</h2>
                <div className="space-y-3">
                  <div className="border border-slate-300 bg-white px-3 py-2">
                    <span className="block text-[11px] uppercase text-slate-500">País</span>
                    <span className="flex items-center justify-between text-sm font-medium text-slate-900">
                      Brasil
                    </span>
                  </div>
                  <Field label="CEP" error={form.formState.errors.address?.postalCode?.message}>
                    <input className="field" type="tel" inputMode="numeric" placeholder="CEP" value={postalCode ?? ""} onChange={handlePostalCodeChange} onBlur={() => lookupCep()} />
                  </Field>
                </div>
              </div>
              <ContinueButton onClick={continueContact}>Continuar</ContinueButton>
            </div>
          </StepSection>

          <StepSection active={activeStep === "shipping"} completed={completedSteps.includes("shipping")} title="Entrega" summary={shippingSummary || "Escolha o frete"} onEdit={() => setActiveStep("shipping")}>
            <div className="space-y-1.5">
              <div className="mb-3 flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-700">Envio em domicílio</span>
                <button type="button" className="font-semibold underline underline-offset-2" onClick={() => setActiveStep("contact")}>
                  Alterar CEP
                </button>
              </div>
              {availableRates?.map((rate) => (
                <label
                  key={rate.id}
                  className={`grid w-full min-w-0 cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border bg-white px-3 py-3 transition ${
                    selectedShippingMethod === rate.id ? "border-ink bg-slate-50" : "border-line hover:border-slate-400"
                  }`}
                >
                  <input type="radio" value={rate.id} className="h-4 w-4 shrink-0 accent-ink" {...form.register("selectedShippingMethod")} />
                  <span className="min-w-0 text-sm">
                    <span className="block truncate font-medium">{rate.label}</span>
                    <span className="block text-xs text-accent">{formatDeliveryTime(rate.id, rate.estimatedDays)}</span>
                  </span>
                  <strong className={`shrink-0 whitespace-nowrap text-sm ${rate.amount === 0 ? "font-bold text-emerald-600 uppercase" : "text-ink"}`}>{rate.amount === 0 ? "Grátis" : formatMoney(rate.amount)}</strong>
                </label>
              ))}
              {ratesQuery.isLoading && !availableRates && <p className="border border-line bg-soft px-3 py-2 text-sm text-slate-600">Calculando opções de entrega...</p>}
              {ratesQuery.isError && !availableRates && (
                <p className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  Não conseguimos calcular o frete agora. Confira o CEP e tente continuar novamente.
                </p>
              )}
              {!availableRates && !ratesQuery.isLoading && !ratesQuery.isError && <p className="text-sm text-slate-500">Informe o CEP para calcular o frete.</p>}
              <div className="space-y-3 pt-7">
                <h2 className="text-sm font-medium uppercase text-slate-900">Dados para entrega</h2>
                  <Field label="Nome" error={form.formState.errors.firstName?.message}>
                    <input className="field" autoComplete="given-name" placeholder="Nome" {...form.register("firstName")} />
                  </Field>
                  <Field label="Sobrenome" error={form.formState.errors.lastName?.message}>
                    <input className="field" autoComplete="family-name" placeholder="Sobrenome" {...form.register("lastName")} />
                  </Field>
                  <Field label="Telefone com DDD" error={form.formState.errors.phone?.message}>
                    <input className="field" type="tel" inputMode="numeric" autoComplete="tel" name="phone" placeholder="Telefone com DDD" value={phone ?? ""} onChange={handlePhoneChange} />
                  </Field>

                  <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                    <Field label="Rua" error={form.formState.errors.address?.street?.message}>
                      <input className="field" autoComplete="address-line1" placeholder="Rua" {...form.register("address.street")} />
                    </Field>
                    <Field label="Bairro" error={form.formState.errors.address?.neighborhood?.message}>
                      <input className="field" placeholder="Bairro" {...form.register("address.neighborhood")} />
                    </Field>
                    <Field label="Cidade" error={form.formState.errors.address?.city?.message}>
                      <input className="field" placeholder="Cidade" {...form.register("address.city")} />
                    </Field>
                    <Field label="Estado" error={form.formState.errors.address?.state?.message}>
                      <input className="field" maxLength={2} placeholder="Estado (UF)" {...form.register("address.state")} />
                    </Field>
                  </div>

                  <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
                    <Field label="Número" error={form.formState.errors.address?.number?.message}>
                      <input
                        className="field"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="address-line2"
                        placeholder="Número"
                        disabled={noNumber}
                        name={numberField.name}
                        onBlur={numberField.onBlur}
                        onChange={numberField.onChange}
                        ref={(element) => {
                          numberField.ref(element);
                          numberInputRef.current = element;
                        }}
                      />
                    </Field>
                    <label className="flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-3 text-xs text-slate-700 sm:justify-center rounded-md">
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 accent-emerald-600"
                        checked={noNumber}
                        onChange={(event) => {
                          setNoNumber(event.target.checked);
                          form.setValue("address.number", event.target.checked ? "S/N" : "", { shouldDirty: true, shouldValidate: true });
                        }}
                      />
                      Sem número
                    </label>
                    <Field label="Apto, Bloco, Referência (opcional)" error={form.formState.errors.address?.complement?.message}>
                      <input className="field" placeholder="Apto, Bloco, Referência (opcional)" {...form.register("address.complement")} />
                    </Field>
                  </div>

                  <ContinueButton onClick={continueShipping}>Continuar</ContinueButton>
                </div>
            </div>
          </StepSection>

          <StepSection active={activeStep === "payment"} completed={false} title="Pagamento" summary="5% de desconto no PIX" onEdit={() => setActiveStep("payment")}>
            <div className="space-y-3">
              <CheckoutReview
                email={contactSummary}
                address={addressSummary}
                phone={phone}
                shipping={shippingSummary}
                onEditContact={() => setEditingReview("contact")}
                onEditAddress={() => setEditingReview("address")}
                onEditShipping={() => setEditingReview("shipping")}
              />
              {editingReview === "contact" && (
                <InlineEdit title="Alterar contato" onDone={() => setEditingReview(null)}>
                  <Field label="E-mail" error={form.formState.errors.email?.message}>
                    <input className="field" type="email" autoComplete="email" placeholder="E-mail" {...form.register("email")} />
                  </Field>
                </InlineEdit>
              )}
              {editingReview === "address" && (
                <InlineEdit title="Alterar entrega" onDone={() => setEditingReview(null)}>
                  <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                    <Field label="Rua" error={form.formState.errors.address?.street?.message}>
                      <input className="field" autoComplete="address-line1" placeholder="Rua" {...form.register("address.street")} />
                    </Field>
                    <Field label="Número" error={form.formState.errors.address?.number?.message}>
                      <input className="field" type="tel" inputMode="numeric" autoComplete="address-line2" placeholder="Número" {...form.register("address.number")} />
                    </Field>
                    <Field label="Bairro" error={form.formState.errors.address?.neighborhood?.message}>
                      <input className="field" placeholder="Bairro" {...form.register("address.neighborhood")} />
                    </Field>
                    <Field label="Cidade" error={form.formState.errors.address?.city?.message}>
                      <input className="field" placeholder="Cidade" {...form.register("address.city")} />
                    </Field>
                    <Field label="Estado" error={form.formState.errors.address?.state?.message}>
                      <input className="field" maxLength={2} placeholder="Estado" {...form.register("address.state")} />
                    </Field>
                    <Field label="Complemento" error={form.formState.errors.address?.complement?.message}>
                      <input className="field" placeholder="Apto, Bloco, Referência, etc. (opcional)" {...form.register("address.complement")} />
                    </Field>
                  </div>
                </InlineEdit>
              )}
              {editingReview === "shipping" && (
                <InlineEdit title="Alterar frete" onDone={() => setEditingReview(null)}>
                  <div className="space-y-1.5">
                    {availableRates?.map((rate) => (
                      <label key={rate.id} className={`grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border bg-white px-3 py-3 ${selectedShippingMethod === rate.id ? "border-ink bg-slate-50" : "border-line"}`}>
                        <input type="radio" value={rate.id} className="h-4 w-4 accent-ink" {...form.register("selectedShippingMethod")} />
                        <span className="text-sm">
                          <span className="block font-medium">{rate.label}</span>
                          <span className="block text-xs text-accent">Chega em {formatDeliveryTime(rate.id, rate.estimatedDays)}</span>
                        </span>
                        <strong className="text-sm text-ink">{formatMoney(rate.amount)}</strong>
                      </label>
                    ))}
                  </div>
                </InlineEdit>
              )}
              <div className="space-y-3 pt-2">
                <h2 className="text-sm font-medium uppercase text-ink">Dados para nota fiscal</h2>
                <Field label="CPF" error={form.formState.errors.cpf?.message}>
                  <input className="field" type="tel" inputMode="numeric" autoComplete="off" name="cpf" placeholder="CPF" value={cpf ?? ""} onChange={handleCpfChange} />
                </Field>
              </div>

              <div className="space-y-3 pt-3">
                <h2 className="text-sm font-medium uppercase text-ink">Forma de pagamento</h2>
                <label className="grid cursor-default grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border border-ink bg-slate-50 px-3 py-4">
                  <QrCode size={20} className="text-ink" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-ink">Pix</span>
                    <span className="block text-xs text-accent">Aprovação rápida com 5% de desconto</span>
                  </span>
                  <input type="radio" checked readOnly className="h-4 w-4 accent-ink" />
                </label>
              </div>
              {error && <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
              <div className="flex w-full sm:justify-end">
                <button disabled={pixMutation.isPending} className="flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-5 py-4 text-base font-bold text-white shadow-sm transition hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-60 sm:w-[380px]">
                  {pixMutation.isPending && <Loader2 size={18} className="animate-spin" />} Finalizar Compra
                </button>
              </div>
            </div>
          </StepSection>
          </div>
        </form>
      }
      aside={<OrderSummary checkout={preview} rates={availableRates} />}
    />
  );
}

function fallbackCheckoutItems() {
  return [
    {
      id: "shipping_preview",
      shopifyProductGid: "gid://shopify/Product/0",
      shopifyVariantGid: "gid://shopify/ProductVariant/0",
      title: "Produto",
      quantity: 1,
      unitPrice: 0,
      discountAmount: 0,
      finalUnitPrice: 0
    }
  ];
}

function formatPostalCode(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

function formatPhone(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function formatCpf(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function formatDeliveryTime(id: string, days: number) {
  if (id === "motoboy") return "Chega no mesmo dia (para compras confirmadas até 14h)";
  if (id === "sedex") return "Chega em 1 a 3 dias úteis";
  if (id === "economico") return "Chega em 3 a 7 dias úteis";
  return `Chega em ${days} dia${days > 1 ? "s" : ""} útei${days > 1 ? "s" : "l"}`;
}

function persistEmail(email: string) {
  localStorage.setItem("melhot_checkout_email", email);
  document.cookie = `melhot_checkout_email=${encodeURIComponent(email)}; Max-Age=15552000; Path=/; SameSite=Lax; Secure`;
}

type StoredCheckoutProfile = {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  selectedShippingMethod?: string;
  address?: {
    postalCode?: string;
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    country?: "BR";
  };
  updatedAt: string;
};

function persistProfile(profile: Partial<Omit<StoredCheckoutProfile, "updatedAt">>) {
  const current = getStoredProfile();
  const next: StoredCheckoutProfile = {
    ...current,
    ...profile,
    address: {
      ...current?.address,
      ...profile.address,
      country: "BR"
    },
    updatedAt: new Date().toISOString()
  };
  localStorage.setItem("melhot_checkout_profile", JSON.stringify(next));
}

function getStoredProfile(): StoredCheckoutProfile | null {
  const raw = localStorage.getItem("melhot_checkout_profile");
  if (!raw) return null;
  try {
    const profile = JSON.parse(raw) as StoredCheckoutProfile;
    if (!profile.updatedAt) return null;
    const maxAgeMs = 180 * 24 * 60 * 60 * 1000;
    if (Date.now() - new Date(profile.updatedAt).getTime() > maxAgeMs) {
      localStorage.removeItem("melhot_checkout_profile");
      return null;
    }
    return profile;
  } catch {
    localStorage.removeItem("melhot_checkout_profile");
    return null;
  }
}

function getStoredEmail() {
  const local = localStorage.getItem("melhot_checkout_email");
  if (local) return local;
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith("melhot_checkout_email="))
    ?.split("=")[1];
  return cookie ? decodeURIComponent(cookie) : "";
}

async function lookupAddressFallback(postalCode: string) {
  const viaCep = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`)
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);

  if (viaCep && !viaCep.erro) {
    return {
      postalCode,
      street: viaCep.logradouro ?? "",
      neighborhood: viaCep.bairro ?? "",
      city: viaCep.localidade ?? "",
      state: viaCep.uf ?? "",
      country: "BR" as const
    };
  }

  const brasilApi = await fetch(`https://brasilapi.com.br/api/cep/v1/${postalCode}`)
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);

  if (!brasilApi) return null;
  return {
    postalCode,
    street: brasilApi.street ?? "",
    neighborhood: brasilApi.neighborhood ?? "",
    city: brasilApi.city ?? "",
    state: brasilApi.state ?? "",
    country: "BR" as const
  };
}

function PromoTimer() {
  const [now, setNow] = useState(Date.now());
  const endAt = useMemo(() => {
    const key = "melhot_offer_timer_end_at";
    const stored = Number(localStorage.getItem(key));
    if (stored > Date.now()) return stored;
    const next = Date.now() + 6 * 60 * 1000;
    localStorage.setItem(key, String(next));
    return next;
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const secondsLeft = Math.max(0, Math.ceil((endAt - now) / 1000));
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <div className="mx-auto mb-4 max-w-2xl bg-gradient-to-r from-hot via-rose-600 to-violet px-4 py-3 text-center text-white rounded-lg shadow-sm">
      <p className="text-sm font-bold leading-none">⚡ OFERTA HOT EXCLUSIVA - APROVEITE ANTES QUE ACABE!</p>
      <p className="mt-1.5 text-xs text-white/90">
        Seu desconto termina em: <strong className="text-base text-white font-mono font-black">00 : 00 : {String(minutes).padStart(2, "0")} : {String(seconds).padStart(2, "0")}</strong>
      </p>
    </div>
  );
}

function CheckoutProgress({ activeStep, completedSteps }: { activeStep: CheckoutStep; completedSteps: CheckoutStep[] }) {
  const paymentActive = activeStep === "payment";
  const deliveryDone = completedSteps.includes("shipping") || paymentActive;
  const items = [
    { id: "cart", label: "Carrinho", done: true, active: false },
    { id: "delivery", label: "Entrega", done: deliveryDone, active: !paymentActive },
    { id: "payment", label: "Pagamento", done: false, active: paymentActive }
  ];

  return (
    <div className="mb-6 border-y border-slate-200 py-4 lg:border-t-0">
      <div className="relative mx-auto grid max-w-2xl grid-cols-3 gap-2">
        <div className="absolute left-[16.5%] right-[16.5%] top-4 h-px bg-slate-300" />
        {items.map((item) => (
          <div key={item.id} className="relative z-10 flex flex-col items-center gap-2 text-center text-xs text-accent">
            <span className={`grid h-8 w-8 place-items-center rounded-full border bg-white ${item.active || item.done ? "border-ink text-ink" : "border-slate-300 text-slate-400"}`}>
              {item.done ? <CheckCircle2 size={16} /> : item.id === "delivery" ? <Truck size={15} /> : item.id === "payment" ? <LockKeyhole size={15} /> : <span className="h-2 w-2 rounded-full bg-current" />}
            </span>
            <span className={item.active ? "font-semibold text-ink" : ""}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CheckoutReview({
  email,
  address,
  phone,
  shipping,
  onEditContact,
  onEditAddress,
  onEditShipping
}: {
  email?: string;
  address?: string;
  phone?: string;
  shipping?: string;
  onEditContact: () => void;
  onEditAddress: () => void;
  onEditShipping: () => void;
}) {
  return (
    <div className="mb-5 divide-y divide-slate-200 border-y border-slate-200 text-sm">
      <ReviewRow icon={<Mail size={16} />} primary={email || "E-mail"} onEdit={onEditContact} />
      <ReviewRow icon={<Pencil size={16} />} primary={address || "Endereço"} secondary={phone} onEdit={onEditAddress} />
      <ReviewRow icon={<Truck size={16} />} primary={shipping || "Entrega"} onEdit={onEditShipping} />
    </div>
  );
}

function ReviewRow({ icon, primary, secondary, onEdit }: { icon: React.ReactNode; primary: string; secondary?: string; onEdit: () => void }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <span className="mt-0.5 text-accent">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{primary}</span>
        {secondary && <span className="block truncate text-xs text-accent">{secondary}</span>}
      </span>
      <button type="button" onClick={onEdit} className="text-sm font-semibold underline underline-offset-2">
        Alterar
      </button>
    </div>
  );
}

function InlineEdit({ title, children, onDone }: { title: string; children: React.ReactNode; onDone: () => void }) {
  return (
    <div className="border border-slate-200 bg-white p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium uppercase text-ink">{title}</h3>
        <button type="button" className="text-sm font-semibold underline underline-offset-2" onClick={onDone}>
          OK
        </button>
      </div>
      {children}
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="sr-only">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

function StepSection({
  active,
  completed,
  icon,
  title,
  summary,
  onEdit,
  children
}: {
  active: boolean;
  completed: boolean;
  icon?: React.ReactNode;
  title: string;
  summary?: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  if (!active && !completed) return null;

  if (!active && completed) {
    return (
      <section className="border-b border-slate-200 pb-4">
        <button type="button" className="flex w-full items-center justify-between gap-3 text-left" onClick={onEdit}>
          <span className="flex min-w-0 items-center gap-3">
            <CheckCircle2 size={19} className="shrink-0 text-trust" />
            <span className="min-w-0">
              <span className="block text-sm font-medium uppercase text-ink">{title}</span>
              {summary && <span className="block truncate text-sm text-accent">{summary}</span>}
            </span>
          </span>
          <span className="text-sm font-semibold underline underline-offset-2">Editar</span>
        </button>
      </section>
    );
  }

  return (
    <section className="border-b border-slate-200 pb-7">
      <div className="mb-3 flex items-center gap-3">
        {icon && <span className="grid h-7 w-7 place-items-center rounded-full bg-soft text-trust">{icon}</span>}
        <h2 className="text-sm font-medium uppercase text-ink">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function ContinueButton({ children, onClick }: { children: React.ReactNode; onClick: () => void | Promise<void> }) {
  return (
    <div className="flex w-full sm:justify-end pt-2">
      <button
        type="button"
        className="w-full rounded-md bg-emerald-600 px-5 py-3.5 text-base font-bold text-white shadow-sm transition hover:bg-emerald-700 active:scale-[0.99] sm:w-[380px]"
        onClick={onClick}
      >
        {children}
      </button>
    </div>
  );
}

